import React from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'clsx';
import PrintProvider, { usePrint } from 'components/ReportsModule/providers/PrintProvider';
import { Box, Grid, Typography, Divider, Button, makeStyles } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import useStore from 'store';
import formatUiDate from 'utilities/format-ui-date';
import makePrintStyles from '../hooks/make-print-styles';
import { BuildAssessmentTypes } from '../keyboarding-report-types';
import { calcMinutes } from '../keyboarding-report-util';
import GradeLevelChip from './GradeLevelChip';
import SubText from './SubText';

type Props = {
  data: BuildAssessmentTypes;
};

const ConstructAResponseModal = ({ data }: Props) => {
  const classes = useStyles();
  const printClasses = usePrintStyles();
  const [, { closeModal }] = useStore();
  const { t } = useTranslation('GradeLevel');
  const [ref, { handlePrint }] = usePrint();
  const getTitle = () => {
    return data?.a1_question ?? data?.a2_question;
  };

  const attempts: any[] = [
    {
      timeToAnswer: data.a1_timeToAnswer ?? null,
      noOfTries: data.a1_noOfTries ?? null,
      ...(data.a1_construct_a_response && {
        constructAResponse: data.a1_construct_a_response
      }),
      ...(data.a1_multi_part && {
        multiPart: data.a1_multi_part
      })
    },

    data?.a2_timeToAnswer && {
      timeToAnswer: data.a2_timeToAnswer ?? null,
      noOfTries: data.a2_noOfTries ?? null,
      ...(data.a2_construct_a_response && {
        constructAResponse: data.a2_construct_a_response
      }),
      ...(data.a2_multi_part && {
        multiPart: data.a2_multi_part
      })
    }
  ];

  const handleLinkClick = (url: string) => {
    window.open(url, '_newtab');
  };

  const handleDownloadResponse = () => {
    const documentTitle = `${data.activity.description} - ${data.student?.firstName} ${data?.student?.lastName}.pdf`;

    handlePrint({
      documentTitle
    });
  };

  return (
    <div ref={ref} className={classes.container}>
      <Box>
        <Box className={printClasses.titleContainer} padding={4}>
          <Typography style={{ fontSize: 28, fontWeight: 450 }} className={printClasses.title}>
            {getTitle()}
          </Typography>
          <Typography className={printClasses.subtitle}>
            <SubText style={{ fontSize: 14, fontWeight: 400 }} className={printClasses.subtitle}>
              Completed on {formatUiDate(data?.dateCompleted!, 'MM/dd/yyyy')}
            </SubText>
          </Typography>
        </Box>
        <Divider />
        <Box className={printClasses.nameContainer} padding={4}>
          <Typography
            variant="h6"
            style={{ fontWeight: 500, display: 'flex' }}
            className={printClasses.title}
          >
            {data?.student?.firstName} {data?.student?.lastName}
            <GradeLevelChip text={t(data?.student?.gradeLevel!)} />
          </Typography>
          <Typography className={printClasses.text}>
            <SubText style={{ fontSize: 12, fontWeight: 400 }} className={printClasses.text}>
              Class
            </SubText>
            <Typography
              style={{ fontSize: 14, fontWeight: 450, display: 'flex' }}
              className={printClasses.text}
            >
              {data?.student?.classrooms?.[0].name ?? ''}
            </Typography>
          </Typography>
        </Box>
        <Divider style={{ margin: '0 32px' }} />
        <Box className={printClasses.titleContainer} padding={4}>
          <Typography
            className={printClasses.text}
            variant="h6"
            style={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}
          >
            {data.activity.title}
            <Button
              className={printClasses.hide}
              variant="text"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => handleLinkClick(data.activity.url)}
            >
              Preview
            </Button>
          </Typography>
          <Typography className={printClasses.text}>{data.activity.description}</Typography>
        </Box>
        <Divider style={{ margin: '0 32px' }} />
        <Box padding={4}>
          <Grid container>
            {attempts?.map((x, idx) => (
              <Grid xs={12}>
                {(x?.constructAResponse || x?.multiPart) && (
                  <Box className={classes.body}>
                    <Box>
                      <Typography className={printClasses.text}>
                        Student Answer - Attempt {idx + 1}
                      </Typography>
                      <Typography>
                        <SubText
                          style={{
                            color: 'black',
                            paddingBottom: 36,
                            fontSize: 14,
                            fontWeight: 400
                          }}
                          className={printClasses.text}
                        >
                          <div
                            className={printClasses.text}
                            dangerouslySetInnerHTML={{
                              __html: x?.constructAResponse || x?.multiPart || ''
                            }}
                          />
                        </SubText>
                      </Typography>
                    </Box>

                    <Box
                      display="flex"
                      width={x?.timeToAnswer > 60 ? 165 : 75}
                      justifyContent="space-between"
                      className={printClasses.printWidth}
                    >
                      {
                        <Typography className={cx(classes.text, printClasses.text)}>
                          {x?.timeToAnswer ? calcMinutes(x.timeToAnswer) : 'N/A'}{' '}
                        </Typography>
                      }
                      <Typography className={cx(classes.text, printClasses.text)}>
                        {x?.noOfTries ? `${x.noOfTries} tries` : 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
      <Box className={classes.footer}>
        <Button style={{ marginRight: 24 }} variant="outlined" onClick={closeModal}>
          Close
        </Button>

        <Button
          style={{ marginRight: 24 }}
          color="primary"
          variant="contained"
          onClick={handleDownloadResponse}
        >
          Download Response
        </Button>
      </Box>
    </div>
  );
};

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-between',
    flexDirection: 'column',
    height: '100%'
  },
  body: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-between'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    height: 68,
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    '@media print': { display: 'none' }
  },
  text: {
    fontSize: '12px',
    fontWeight: 450
  }
}));

const usePrintStyles = makePrintStyles({
  title: {
    fontSize: '36px !important'
  },
  subtitle: {
    paddingTop: '4px !important',
    fontSize: '28px !important'
  },
  text: {
    fontSize: '24px !important',
    width: '100% !important'
  },
  printWidth: {
    width: '200px !important'
  },
  titleContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: 70
  },
  nameContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: 100
  },
  hide: {
    display: 'none'
  }
});

export default (props: any) => (
  <PrintProvider>
    <ConstructAResponseModal {...props} />
  </PrintProvider>
);
